import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Pencil, Plus, X } from '../../../../../components/icon/solar-icons.js'
import { Section } from '../../../../../components/section/section.js'
import type { EmployeeDocument, RequestConfirmFn } from '../employee-profile-types.js'
import '../employee-profile-view.css'

interface DocumentForm {
    name: string
    drive_url: string
    category: string
}

interface DocumentFoldersSectionProps {
    documents: EmployeeDocument[]
    lang: Language
    // Mutation callbacks are optional: when absent (a viewer who may see but not
    // manage this person's documents) the section renders a read-only list.
    onAddDocument?: (data: DocumentForm) => Promise<boolean>
    onUpdateDocument?: (
        documentId: number,
        data: { name: string; drive_url?: string; category: string },
    ) => Promise<boolean>
    onDeleteDocument?: (documentId: number) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

const emptyForm = (): DocumentForm => ({ name: '', drive_url: '', category: 'other' })

/** File-backed documents (Humaans imports) have no Drive URL — only name/category edit. */
const isFileDocument = (doc: EmployeeDocument): boolean => !doc.drive_url && !!doc.file_url

/**
 * Admin/manager UI for managing an employee's documents: Google Drive folder
 * links and Humaans-imported documents (links or stored files) — rows in
 * `portal_employee_documents`. Presentational — all persistence happens
 * through the callbacks; local state covers add/edit forms only.
 */
export const DocumentFoldersSection = ({
    documents,
    lang,
    onAddDocument,
    onUpdateDocument,
    onDeleteDocument,
    requestConfirm,
}: DocumentFoldersSectionProps) => {
    const ph = t.portal

    const [showAddForm, setShowAddForm] = useState(false)
    const [addForm, setAddForm] = useState<DocumentForm>(emptyForm())
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editForm, setEditForm] = useState<DocumentForm>(emptyForm())
    const [saving, setSaving] = useState(false)

    const resetAdd = () => {
        setShowAddForm(false)
        setAddForm(emptyForm())
    }

    const submitAdd = async () => {
        if (!onAddDocument || !addForm.name.trim() || !addForm.drive_url.trim()) return
        setSaving(true)
        const ok = await onAddDocument({
            name: addForm.name.trim(),
            drive_url: addForm.drive_url.trim(),
            category: addForm.category.trim() || 'other',
        })
        setSaving(false)
        if (ok) resetAdd()
    }

    const submitEdit = async (doc: EmployeeDocument) => {
        const fileDoc = isFileDocument(doc)
        if (!onUpdateDocument || !editForm.name.trim() || (!fileDoc && !editForm.drive_url.trim()))
            return
        setSaving(true)
        const ok = await onUpdateDocument(doc.id, {
            name: editForm.name.trim(),
            ...(fileDoc ? {} : { drive_url: editForm.drive_url.trim() }),
            category: editForm.category.trim() || 'other',
        })
        setSaving(false)
        if (ok) setEditingId(null)
    }

    const renderForm = (
        form: DocumentForm,
        setForm: (f: DocumentForm) => void,
        onSubmit: () => void,
        onCancel: () => void,
        withUrl: boolean,
    ) => (
        <div className="portal-profile__contact-form">
            <div className="portal-profile__edit-field">
                <TextField
                    label={ph.profile.folderName(lang)}
                    isRequired
                    value={form.name}
                    onChange={(value) => setForm({ ...form, name: value })}
                />
            </div>
            {withUrl && (
                <div className="portal-profile__edit-field">
                    <TextField
                        label={ph.profile.driveFolderUrl(lang)}
                        isRequired
                        inputProps={{ type: 'url' }}
                        placeholder="https://drive.google.com/drive/folders/..."
                        value={form.drive_url}
                        onChange={(value) => setForm({ ...form, drive_url: value })}
                    />
                </div>
            )}
            <div className="portal-profile__edit-actions">
                <Button
                    variant="tertiary"
                    size="small"
                    text={ph.profile.cancelEdit(lang)}
                    isDisabled={saving}
                    onClick={onCancel}
                />
                <Button
                    variant="primary"
                    size="small"
                    text={ph.profile.save(lang)}
                    isDisabled={saving || !form.name.trim() || (withUrl && !form.drive_url.trim())}
                    onClick={onSubmit}
                />
            </div>
        </div>
    )

    return (
        <Section>
            {onAddDocument && !showAddForm && editingId === null && (
                <div style={{ marginBottom: 12 }}>
                    <Button
                        variant="tertiary"
                        size="small"
                        iconLeft={Plus}
                        text={ph.profile.addFolder(lang)}
                        onClick={() => setShowAddForm(true)}
                    />
                </div>
            )}

            {documents.length === 0 && !showAddForm && (
                <div className="portal-profile__field-value">{ph.profile.noFolders(lang)}</div>
            )}

            {documents.map((doc) =>
                editingId === doc.id ? (
                    <div key={doc.id}>
                        {renderForm(
                            editForm,
                            setEditForm,
                            () => void submitEdit(doc),
                            () => setEditingId(null),
                            !isFileDocument(doc),
                        )}
                    </div>
                ) : (
                    <div key={doc.id} className="portal-profile__doc-row">
                        <div className="portal-profile__doc-info">
                            {doc.drive_url || doc.file_url ? (
                                <a
                                    className="portal-profile__doc-name"
                                    href={doc.drive_url ?? doc.file_url ?? undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {doc.name}
                                </a>
                            ) : (
                                <span className="portal-profile__doc-name">{doc.name}</span>
                            )}
                        </div>
                        {(onUpdateDocument || onDeleteDocument) && (
                            <div className="portal-profile__doc-actions">
                                {onUpdateDocument && (
                                    <Button
                                        className="portal-profile__icon-btn"
                                        variant="tertiary"
                                        size="small"
                                        iconOnly
                                        iconLeft={Pencil}
                                        text={ph.profile.editFolder(lang)}
                                        buttonProps={
                                            {
                                                'data-tooltip': ph.profile.editFolder(lang),
                                            } as React.ButtonHTMLAttributes<HTMLButtonElement>
                                        }
                                        onClick={() => {
                                            setEditingId(doc.id)
                                            setEditForm({
                                                name: doc.name,
                                                drive_url: doc.drive_url ?? '',
                                                category: doc.category,
                                            })
                                        }}
                                    />
                                )}
                                {onDeleteDocument && (
                                    <Button
                                        className="portal-profile__icon-btn"
                                        variant="tertiary"
                                        size="small"
                                        iconOnly
                                        iconLeft={X}
                                        text={ph.profile.deleteFolder(lang)}
                                        buttonProps={
                                            {
                                                'data-tooltip': ph.profile.deleteFolder(lang),
                                            } as React.ButtonHTMLAttributes<HTMLButtonElement>
                                        }
                                        onClick={() =>
                                            requestConfirm(
                                                ph.profile.confirmDeleteFolder(lang),
                                                () => {
                                                    void onDeleteDocument(doc.id)
                                                },
                                            )
                                        }
                                    />
                                )}
                            </div>
                        )}
                    </div>
                ),
            )}

            {showAddForm && renderForm(addForm, setAddForm, () => void submitAdd(), resetAdd, true)}
        </Section>
    )
}
